import React from 'react';

import { useSyncExternalStore } from 'use-sync-external-store/shim';
import type { UseBoundStore } from 'zustand/react';
import {
  createStore,
  StoreApi,
  StateCreator,
  Mutate,
  StoreMutatorIdentifier,
} from 'zustand/vanilla';

/**
 * The key name used to store all global Alloy stores on the window object.
 * @internal
 */
export const globalAlloyStoresKeyName = '__AlloyStores__';

/**
 * Global `Window` extension for Alloy's shared Zustand stores.
 *
 * The map holds **vanilla** `StoreApi` instances keyed by store name (`unknown`
 * at rest because the object must hold heterogeneous state shapes). Bound
 * React hooks are **not** cached here; see `createBoundStoreHook` and
 * `getOrCreateGlobalStore` for why wrappers are created per caller.
 */
declare global {
  interface Window {
    [globalAlloyStoresKeyName]?: {
      [key: string]: unknown;
    };
  }
}

/** React 17 lacks `useSyncExternalStore`; we may attach the shim from `use-sync-external-store`. */
type ReactWithPolyfill = typeof React & {
  useSyncExternalStore?: typeof useSyncExternalStore;
};

/**
 * Polyfills `useSyncExternalStore` on the React namespace for React 17.
 * Newer React versions already provide the hook natively.
 */
function syncExternalStorePolyfill() {
  const reactWithPolyfill = React as ReactWithPolyfill;
  if (!reactWithPolyfill.useSyncExternalStore) {
    reactWithPolyfill.useSyncExternalStore = useSyncExternalStore;
  }
}

syncExternalStorePolyfill();

/** Default selector for bound hooks when none is passed. @internal */
const identity = <S>(arg: S): S => arg;

/**
 * Creates a UseBoundStore hook wrapper around a vanilla Zustand store,
 * using THIS module's React import for hook binding. In Module Federation
 * bridge setups each side (host/remote) has its own copy of this module
 * with its own React, so hooks always bind to the correct dispatcher.
 */
export function createBoundStoreHook<T>(
  vanillaStore: StoreApi<T>,
): UseBoundStore<StoreApi<T>> {
  const useSyncStore = (React as ReactWithPolyfill)?.useSyncExternalStore;
  const useBoundStore = <U>(
    selector: (state: T) => U = identity as (state: T) => U,
  ) => {
    const getSnapshot = React.useCallback(
      () => selector(vanillaStore.getState()),
      [selector],
    );
    const getServerSnapshot = React.useCallback(
      () => selector(vanillaStore.getInitialState()),
      [selector],
    );
    const slice = useSyncStore(
      vanillaStore.subscribe,
      getSnapshot,
      getServerSnapshot,
    );
    React.useDebugValue(slice);
    return slice;
  };
  return Object.assign(useBoundStore, vanillaStore) as UseBoundStore<
    StoreApi<T>
  >;
}

/**
 * Gets or creates a global Zustand store by name and returns a **bound** hook
 * for this module's React instance.
 *
 * The vanilla store is created with `createStore` and kept on `window` under
 * `globalAlloyStoresKeyName`, so there is only one underlying store per name
 * app-wide. The returned `UseBoundStore` wrapper is built with
 * `createBoundStoreHook` on **each** call (not stored on `window`), so in
 * Module Federation each host/remote copy of this module binds hooks to its own
 * React dispatcher.
 *
 * @template T - State shape of the store.
 * @template Mutators - Optional Zustand mutator tuples for middleware typing.
 *   Each entry is `[StoreMutatorIdentifier, unknown]` because middleware
 *   configs differ; the identifier carries the discriminant Zustand expects.
 *
 * @param storeCreator - Vanilla `StateCreator` for the store.
 * @param name - Unique global name for this store.
 * @returns A `UseBoundStore` hook (and store API surface) for `T` / `Mutators`.
 *
 * @example
 * ```ts
 * type CounterStore = { count: number; inc: () => void };
 *
 * const useCounterStore = getOrCreateGlobalStore<CounterStore>(
 *   (set, get) => ({
 *     count: 0,
 *     inc: () => set({ count: get().count + 1 }),
 *   }),
 *   'CounterStore',
 * );
 *
 * const count = useCounterStore((s) => s.count);
 * const inc = useCounterStore((s) => s.inc);
 * ```
 */
export function getOrCreateGlobalStore<
  T,
  Mutators extends [StoreMutatorIdentifier, unknown][] = [],
>(
  storeCreator: StateCreator<T, [], [], T> | StateCreator<T, [], Mutators, T>,
  name: string,
): UseBoundStore<Mutate<StoreApi<T>, Mutators>> {
  const stores = window[globalAlloyStoresKeyName] || {};
  if (!window[globalAlloyStoresKeyName]) {
    window[globalAlloyStoresKeyName] = stores;
  }
  if (!stores[name]) {
    stores[name] = createStore(storeCreator as StateCreator<T, [], Mutators>);
  }
  // Wrapper is created fresh every call — never cached on window — so
  // each MF remote's copy of this module binds hooks to its own React.
  return createBoundStoreHook(stores[name] as StoreApi<T>) as UseBoundStore<
    Mutate<StoreApi<T>, Mutators>
  >;
}

/**
 * Always creates a global Zustand store by name and returns the vanilla store API.
 * This is useful for tests when we want to create a new store instance for each test.
 * @template T - State shape of the store.
 * @template Mutators - Optional mutator tuples; same semantics as
 *   `getOrCreateGlobalStore`.
 *
 * @param storeCreator - Vanilla `StateCreator` for the store.
 * @param name - Unique global name for this store.
 * @returns The vanilla store API for `T` / `Mutators`.
 */
export function createGlobalStore<
  T,
  Mutators extends [StoreMutatorIdentifier, unknown][] = [],
>(storeCreator: StateCreator<T, [], Mutators, T>, name: string): StoreApi<T> {
  const stores = window[globalAlloyStoresKeyName] || {};
  if (!window[globalAlloyStoresKeyName]) {
    window[globalAlloyStoresKeyName] = stores;
  }
  stores[name] = createStore(storeCreator as StateCreator<T, [], Mutators>);
  return stores[name] as StoreApi<T>;
}

/**
 * Returns a **bound** hook for an existing global store by name, using this
 * module's React. Does **not** create a store; use `getOrCreateGlobalStore`
 * where initialization should happen.
 *
 * If no store exists for `name`, the implementation returns `undefined` at
 * runtime; the return type is still asserted as `UseBoundStore` for call-site
 * ergonomics; callers that need strict optional handling should ensure the
 * store was created first.
 *
 * Like `getOrCreateGlobalStore`, the hook wrapper is created on each call so
 * federated bundles each bind to their own React.
 *
 * @template T - State shape of the store.
 * @template Mutators - Optional mutator tuples; same semantics as
 *   `getOrCreateGlobalStore`.
 *
 * @param name - Global store name.
 * @returns Bound `UseBoundStore` for the vanilla store at `name`, or
 *   `undefined` at runtime if missing.
 *
 * @example
 * ```ts
 * type CounterStore = { count: number; inc: () => void };
 *
 * // Elsewhere: getOrCreateGlobalStore<CounterStore>(..., 'CounterStore');
 *
 * const useCounterStore = useGlobalStore<CounterStore>('CounterStore');
 * const count = useCounterStore((s) => s.count);
 * ```
 */
export function useGlobalStore<
  T,
  Mutators extends [StoreMutatorIdentifier, unknown][] = [],
>(name: string): UseBoundStore<Mutate<StoreApi<T>, Mutators>> {
  const stores = window[globalAlloyStoresKeyName] || {};
  const vanillaStore = stores[name] as StoreApi<T> | undefined;
  if (!vanillaStore) {
    return undefined as unknown as UseBoundStore<Mutate<StoreApi<T>, Mutators>>;
  }
  // Fresh wrapper each call — uses this module's React, not the host's.
  return createBoundStoreHook(vanillaStore) as UseBoundStore<
    Mutate<StoreApi<T>, Mutators>
  >;
}

/**
 * Clears all global Zustand stores from the window object.
 */
export const clearGlobalStores = () => {
  window[globalAlloyStoresKeyName] = {};
};
